import type { Edge, Node } from '@xyflow/react';

import type { StoryboardBeatInput, VideoAspectRatio } from '@/store/slices/creativeJobs';

import { STORYBOARD_MIN_BEATS, type StoryboardDraft } from '../../storyboard';

/**
 * Pure derivation of the React Flow graph from a {@link StoryboardDraft}. The draft is the single
 * source of truth; nodes/edges are recomputed on every change, preserving each node's dragged
 * position by id. Layout: Key visual (F0, locked origin) -> Beat 1 -> Beat 2 -> ... -> Beat N ->
 * Output. Frames are NOT user-facing nodes: each beat's end frame is derived automatically (and the
 * worker seams beat i's ACTUAL last rendered frame into beat i+1's start), so the canvas shows only
 * beats. No React, no store: unit-testable in isolation.
 */

/** A node's run state, driving the canvas choreography while a generation is in flight. `idle` is also
 *  the resting state when nothing is running (rendered neutral, exactly as before). `stale` marks a
 *  beat/frame at or past a regenerate/resume's `fromBeat` that is queued to be re-run. */
export type NodeStatus = 'idle' | 'active' | 'done' | 'stale';

/** The origin (F0) node's data: the locked key visual the first beat animates from. Frames are no
 *  longer their own nodes (only F0 remains as a read-only origin), but the type keeps the wider shape
 *  for the run choreography that lights F0 up. */
export type FrameNodeData = {
  index: number;
  imageUrl?: string;
  source: 'keyArt' | 'generate' | 'url';
  prompt?: string;
  url?: string;
  locked: boolean;
  status: NodeStatus;
  /** width / height of the finished video, so the thumbnail matches the output orientation (portrait
   *  9:16 vs landscape 16:9) instead of a fixed 16:9 box. */
  thumbRatio: number;
  /** This beat's rendered clip URL, once the run has produced it (`segments[index]`); drives the
   *  frame's view affordance. Absent for F0 (the key visual has no clip). */
  clipUrl?: string;
  /** Whether a generation is in flight, so the node's Regenerate control can self-gate + disable. */
  running: boolean;
  /** Multi-promo only (2+ promotions): the 0-based promotion this key visual belongs to, so the card can
   *  label itself "Key visual N". Undefined for a single-promo ad (the card reads plain "Key visual"). */
  promotionIndex?: number;
  /** Multi-promo only: the promotion's headline, shown as a caption so the owner can tell them apart. */
  promotionName?: string;
};

/** A beat node's data. `index` is the 0-based beat array index. */
export type BeatNodeData = {
  index: number;
  beat: StoryboardBeatInput;
  canRemove: boolean;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  status: NodeStatus;
  /** This beat's rendered clip URL, once the run has produced it (`segments[index + 1]`); drives the
   *  beat's view affordance. */
  clipUrl?: string;
  /** This beat's end-frame image (`generatedFrames[index + 1]`), used as the card's video-preview
   *  poster + the modal's clip poster. Absent until the beat's end frame has been generated. */
  poster?: string;
  /** Whether a generation is in flight, so the node's Regenerate control can self-gate + disable. */
  running: boolean;
  /** width / height of the finished video, so the preview thumbnail matches the output orientation. */
  thumbRatio: number;
  /** Set on the beat a FAILED run stopped at: a short, owner-facing reason shown under the card so it's
   *  clear WHERE (and why) generation failed. Absent unless this beat is the failure point. */
  error?: string;
  /** Compose loader state for this beat while the storyboard is being written: 'skeleton' (a shimmer
   *  placeholder, waiting on the model or a not-yet-reached beat), 'typing' (its motion prose is animating
   *  in), or undefined (a normal, editable beat). Drives BeatNode's placeholder render. */
  composing?: 'skeleton' | 'typing';
  /** While `composing==='typing'`, the motion prose revealed so far (the simulated typewriter). */
  typedMotion?: string;
  /** Multi-promo only (2+ promotions): the 0-based promotion this beat advertises, so the card can show a
   *  "Promo N" chip. Undefined for a single-promo ad (no chip, unchanged look). */
  promotionIndex?: number;
  /** Multi-promo only: the promotion's headline for this beat's chip (falls back to the ordinal). */
  promotionName?: string;
};

/** The brand node's data: the logo + domain that get baked into the ad, shown as a small input card
 *  beneath the key visual and wired into the first beat. */
export type BrandNodeData = {
  logoUrl?: string;
  domain?: string;
  status: NodeStatus;
};

/** One version of the ad (newest first), for the output node's version picker: a storyboard + its optional
 *  video (url absent = a storyboard-only version, composed but not yet rendered). Keyed by `key` (its
 *  created_at), since a storyboard-only version has no url to key on. `storyboard` is opaque here (the composer
 *  loads it into the editor on select). */
export type OutputVersion = { key: string; url?: string; storyboard?: unknown; createdAt: number };

/** The output node's data: aspect, end-card CTA, a read-only summary, AND the finished ad itself once it has
 *  rendered (the selected version plays inline in the card, with a picker over the version history). */
export type OutputNodeData = {
  aspect: VideoAspectRatio;
  /** The aspect ratios the chosen model can produce (for the picker); absent = the default 16:9 / 9:16. */
  aspects?: VideoAspectRatio[];
  cta: string;
  beats: number;
  seconds: number;
  status: NodeStatus;
  /** The ad's versions (storyboard + optional video), newest first (empty/absent for a never-composed ad). */
  versions?: OutputVersion[];
  /** The key of the version currently selected (defaults to the newest); undefined = none yet. */
  selectedVersionKey?: string;
  /** Poster shown before the video plays (the ad's key visual). */
  poster?: string;
  /** Base filename for the download control (built by the composer via assetFilename). */
  downloadName?: string;
};

export type FrameFlowNode = Node<FrameNodeData, 'frame'>;
export type BeatFlowNode = Node<BeatNodeData, 'beat'>;
export type BrandFlowNode = Node<BrandNodeData, 'brand'>;
export type OutputFlowNode = Node<OutputNodeData, 'output'>;
/** The on-canvas control card (promotion / prompt / regenerate). It carries no draft-derived data: its
 *  content is supplied by the composer through PromoPanelContext (see PromoNode), so the live prompt value
 *  never lags a render. */
export type PromoNodeData = Record<string, never>;
export type PromoFlowNode = Node<PromoNodeData, 'promo'>;
export type StoryboardNode = FrameFlowNode | BeatFlowNode | BrandFlowNode | OutputFlowNode | PromoFlowNode;

/** The live-run inputs the graph is choreographed from. All optional: absent = a resting (idle) graph. */
export interface DeriveGraphOpts {
  /** Index-aligned DISPLAY image URLs from the running job (frames[i] = beat i's generated end frame). */
  generatedFrames?: string[];
  /** The id of the node currently being worked (`frame-<i>` | `beat-<i>` | `assembling` | `done`). */
  stage?: string;
  /** Whether a generation is in flight (drives whether any node lights up at all). */
  running?: boolean;
  /** Index-aligned CLIP urls from the running/last job (`segments[j]` = beat j's clip), exposed on the
   *  beat/frame nodes for their view affordance. `segments[0]` is `''` (the key visual has no clip). */
  segments?: string[];
  /** The 1-based beat a regenerate/resume run starts from: beats j >= this are marked `stale` (queued
   *  to be re-run) unless the live stage already makes them active/done. Absent = nothing stale. */
  staleFromBeat?: number;
  /** Brand identity shown on the F0 origin card (also baked into the ad's logo bug + end-card). */
  brandLogoUrl?: string;
  brandDomain?: string;
  /** The 1-based beat a FAILED run stopped at, and a short owner-facing reason to show under that beat's
   *  card (so the failure is pinned to WHERE it happened). Both absent unless the run failed. */
  failedBeat?: number;
  beatError?: string;
  /** The aspect ratios the chosen video model can produce, surfaced on the output node's aspect picker so it
   *  only offers what the model supports (start-frame models get all; Veo/Omni get 16:9 / 9:16). */
  videoAspects?: VideoAspectRatio[];
  /** Immersive editor: emit the on-canvas control card (promotion / prompt / regenerate) to the LEFT of F0,
   *  tied to it by a connector. Off on mobile, where the controls stack outside the canvas instead. */
  withControls?: boolean;
  /** Compose loader: the storyboard is being written by the LLM. `step:-1` = seeding (every beat renders as
   *  a shimmer skeleton while the model runs, and the whole chain's connectors pulse). `step>=0` = the
   *  0-based beat whose motion prose is currently typing in (beats before it are done/filled, after it are
   *  skeletons); `typed` = chars of that beat's motion revealed so far. Absent = a normal, resting graph. */
  compose?: { step: number; typed: number };
  /** The finished ad, once rendered: the version history (newest first), which one is selected, and the
   *  poster + download name. Surfaced on the OUTPUT node so the ad plays inside the "Finished ad" card. */
  finishedAd?: {
    versions?: OutputVersion[];
    selectedVersionKey?: string;
    poster?: string;
    downloadName?: string;
  };
}

/** width / height of a video aspect, for sizing preview thumbnails to the output orientation. */
export const aspectRatioValue = (aspect: VideoAspectRatio): number => {
  const [w, h] = aspect.split(':').map(Number);
  return h ? w / h : 16 / 9;
};

/** Horizontal spacing between nodes in the default (un-dragged) layout. */
export const NODE_GAP_X = 320;
/** How far LEFT of F0 the on-canvas control card sits (its own width plus a gap for the connector). */
const PROMO_GAP_X = 380;
// Widths of the two left-column cards (must match BrandNode / PromoNode `$width`). The Setup card is wider,
// so its right edge sits 80px right of the brand card's. We RIGHT-ALIGN the brand card to the Setup card
// (below) so the brand -> beat-1 connector drops its vertical run through the clear channel BETWEEN the Setup
// card's right edge and beat-1, instead of grazing straight down the Setup card's edge (a smoothstep routes its
// vertical segment at the midpoint of the two handles; left-aligned, that midpoint landed exactly on the edge).
const BRAND_CARD_W = 220;
const PROMO_CARD_W = 300;
// The brand card's x: right-aligned with the Setup card so their right edges share a column and the connector
// leaves that shared edge into the gap before beat-1.
const BRAND_X = -PROMO_GAP_X + (PROMO_CARD_W - BRAND_CARD_W);
const FRAME_Y = 0;
// Beats sit on the same row as the key-visual origin and the output — a single clean line now that
// frames are no longer interleaved as their own nodes.
const BEAT_Y = 0;
const OUTPUT_Y = 0;
// The FrameNode / key-visual card ($width 220, ~196px content) shows a thumbnail that is portrait-capped at
// 240px, else ~196/ratio tall (nodes.styles thumbSizing), plus ~92px of chrome (label row, caption, padding).
// The brand card (below F0) and the multi-promo key-visual cards (above their beat) are offset by this COMPUTED
// card height + a gap, so EVERY aspect clears the beat row. A fixed portrait/landscape offset was not enough: a
// 9:16 card barely overlapped, and a 1:1 / 4:3 thumb (taller than 16:9 but not "portrait") overlapped outright.
const FRAME_THUMB_MAX = 240;
const FRAME_CONTENT_W = 196;
const FRAME_CHROME = 92;
const LAYOUT_GAP = 44;
const frameCardHeight = (ratio: number): number =>
  (ratio < 1 ? FRAME_THUMB_MAX : Math.round(FRAME_CONTENT_W / ratio)) + FRAME_CHROME;

/** The active phase parsed from a `stage` id: which 1-based beat is being worked, and where in the run. */
type RunPhase = { activeBeat: number; phase: 'frame' | 'beat' | 'assembling' | 'done' | 'idle' };

const parseStage = (stage: string | undefined): RunPhase => {
  if (stage === 'assembling') return { activeBeat: 0, phase: 'assembling' };
  if (stage === 'done') return { activeBeat: 0, phase: 'done' };
  const match = /^(frame|beat)-(\d+)$/.exec(stage ?? '');
  if (match) return { activeBeat: Number(match[2]), phase: match[1] === 'frame' ? 'frame' : 'beat' };
  return { activeBeat: 0, phase: 'idle' };
};

/** Status of beat j / frame j (they share it): done once the run is past them, active on the current
 *  beat, idle otherwise. `assembling`/`done` means every beat is behind us. */
const beatFrameStatus = (j: number, running: boolean, run: RunPhase): NodeStatus => {
  if (!running) return 'idle';
  if (run.phase === 'assembling' || run.phase === 'done') return 'done';
  if (run.phase === 'idle') return 'idle';
  if (j < run.activeBeat) return 'done';
  if (j === run.activeBeat) return 'active';
  return 'idle';
};

/** The run status of beat `index` (0-based) — done once the run is past it, active while it renders, idle
 *  otherwise. The SAME rule the canvas nodes use (beatFrameStatus), exported so the easy-mode timeline can
 *  highlight the beat currently generating identically, from just `running` + the job's `stage`. */
export const beatRunStatus = (index: number, running: boolean, stage: string | undefined): NodeStatus =>
  beatFrameStatus(index + 1, running, running ? parseStage(stage) : { activeBeat: 0, phase: 'idle' });

/** Compose loader per-beat resolution for beat `i` (0-based): its placeholder `mode`, the card `status`
 *  (drives the chrome), and its inbound-edge status. `step:-1` is the seeding phase (every beat a skeleton,
 *  but the whole chain's connectors pulse so the board reads as "working"); `step>=0` reveals left-to-right,
 *  the cursor beat typing (active) while earlier beats are done and later ones stay skeletons. */
const deriveComposeBeat = (
  i: number,
  compose: { step: number; typed: number },
): { mode?: 'skeleton' | 'typing'; status: NodeStatus; edge: NodeStatus } => {
  if (compose.step < 0) return { mode: 'skeleton', status: 'idle', edge: 'active' };
  // Already revealed: a normal, fully-rendered card (no placeholder) reading as done.
  if (i < compose.step) return { status: 'done', edge: 'done' };
  if (i === compose.step) return { mode: 'typing', status: 'active', edge: 'active' };
  return { mode: 'skeleton', status: 'idle', edge: 'idle' };
};

const outputStatus = (running: boolean, run: RunPhase): NodeStatus => {
  if (!running) return 'idle';
  if (run.phase === 'assembling') return 'active';
  if (run.phase === 'done') return 'done';
  return 'idle';
};

/** An edge inherits its TARGET node's status: the connector leading INTO the active node animates,
 *  connectors into completed nodes read as a subtle solid, and connectors into stale nodes take the
 *  stale (dashed, warning-tone) treatment. */
const edge = (
  source: string,
  target: string,
  status: NodeStatus,
  sourceHandle?: string,
  targetHandle?: string,
): Edge => ({
  id: `e-${source}-${target}`,
  source,
  target,
  sourceHandle,
  targetHandle,
  type: 'smoothstep',
  animated: status === 'active',
  className:
    status === 'active'
      ? 'sb-edge-active'
      : status === 'done'
        ? 'sb-edge-done'
        : status === 'stale'
          ? 'sb-edge-stale'
          : undefined,
  data: { status },
});

/**
 * Derive nodes + edges from a draft. `prev` supplies the last-known node positions (kept by id) so a
 * dragged node stays put across an edit; `keyArt` is F0's thumbnail. `opts` carries the live run so the
 * generated end frames appear as they arrive and the current node/connectors light up.
 */
export const deriveGraph = (
  draft: StoryboardDraft,
  keyArt: string | undefined,
  prev: StoryboardNode[],
  opts: DeriveGraphOpts = {},
): { nodes: StoryboardNode[]; edges: Edge[] } => {
  const prevById = new Map(prev.map((node) => [node.id, node]));
  const n = draft.beats.length;
  const nodes: StoryboardNode[] = [];
  const edges: Edge[] = [];

  const running = Boolean(opts.running);
  const run = running ? parseStage(opts.stage) : { activeBeat: 0, phase: 'idle' as const };
  const generatedFrames = opts.generatedFrames;
  // Compose loader: while the storyboard is being written, the origin (key visual / brand / promo) reads as
  // settled `done` (it is the locked source the beats are built from), exactly as it does during a run.
  const compose = opts.compose;
  const composing = Boolean(compose);
  const originStatus: NodeStatus = running || composing ? 'done' : 'idle';
  // Thumbnails match the finished video's orientation (portrait vs landscape), not a fixed 16:9 box.
  const thumbRatio = aspectRatioValue(draft.aspect);
  // Every promotion's key visual (F0 included) sits one row ABOVE its beat and feeds it top-down, offset by the
  // frame card's actual height for this aspect + a gap so a tall thumbnail clears the beat row.
  const kvAboveY = -(frameCardHeight(thumbRatio) + LAYOUT_GAP);
  // Multi-promo: a storyboard can advertise several promotions, distributed across the beats. With 2+, each
  // beat labels its promotion and every promotion after the first drops in as its own key-visual card; a
  // single-promo ad keeps the plain look (these fields stay undefined). promotions[0] is always F0.
  const promotions = draft.promotions;
  const multiPromo = promotions.length > 1;
  const clampPromo = (idx: number): number =>
    promotions.length === 0 ? 0 : Math.min(Math.max(idx, 0), promotions.length - 1);
  const promoName = (p: number): string | undefined =>
    multiPromo ? promotions[p]?.headline?.trim() || undefined : undefined;
  // First beat (0-based) of each promotion's contiguous run: where that promotion's key-visual card feeds in.
  const promoFirstBeat = new Map<number, number>();

  let order = 0;
  // Carry over position AND React Flow's transient state (selection drives the beat toolbar's
  // visibility; measured is the cached size) by id, so re-deriving on every keystroke never
  // deselects a node or forces a re-measure.
  // Deterministic left-to-right layout: the position is ALWAYS recomputed from the node's order, so
  // cards never overlap — even when the LLM refine restructures the beats (a carried-over position would
  // go stale against the new node set and collide, e.g. the output landing on a newly-added last beat).
  // Only React Flow's transient selection/measure state is carried, so typing focus + sizes survive a
  // re-derive. (Nodes are not user-draggable to a new slot; the graph is a view over a linear draft.)
  const carryOver = (id: string, y: number): Pick<Node, 'position' | 'selected' | 'measured'> => {
    const old = prevById.get(id);
    return { position: { x: NODE_GAP_X * order, y }, selected: old?.selected, measured: old?.measured };
  };

  const f0Id = 'frame-0';
  nodes.push({
    id: f0Id,
    type: 'frame',
    // Above the FIRST beat (same column, one row up), consistent with the other promotions' key visuals, rather
    // than as a leftmost origin. It shares beat 0's order slot (no `order += 1`), so beat 0 stays the left end.
    ...carryOver(f0Id, kvAboveY),
    // F0's key visual is settled the moment a run starts, so it reads as done while running. It has no
    // clip (segments[0] === '') and gets no Regenerate control (see FrameNode); it is the source.
    data: {
      index: 0,
      imageUrl: keyArt,
      source: 'keyArt',
      locked: true,
      status: originStatus,
      running,
      thumbRatio,
      promotionIndex: multiPromo ? 0 : undefined,
      promotionName: promoName(0),
    },
  });
  // F0 does NOT advance `order`: it hangs above beat 0 (which keeps order 0 = the left end of the beat row).

  // The on-canvas control card (immersive only): promotion + template + prompt + regenerate, placed to the
  // LEFT of F0 and tied to it by a connector. It advances no `order` (fixed negative-x column) and carries no
  // data (PromoNode renders content from PromoPanelContext). draggable:false anchors it; it stays SELECTABLE
  // on purpose -- React Flow sets pointer-events:none on a node that is neither draggable nor selectable nor
  // connectable, which would make the card's inputs dead (the selection itself is cosmetic here).
  if (opts.withControls) {
    const promoId = 'promo';
    const oldPromo = prevById.get(promoId);
    nodes.push({
      id: promoId,
      type: 'promo',
      position: { x: -PROMO_GAP_X, y: FRAME_Y },
      selected: oldPromo?.selected,
      measured: oldPromo?.measured,
      draggable: false,
      data: {},
    });
    // Into F0's LEFT target handle ('in'); F0's bottom source ('out') separately feeds beat 0 top-down.
    edges.push(edge(promoId, f0Id, originStatus, undefined, 'in'));
  }

  // The brand identity input: a small card beneath the key visual carrying the logo + domain baked into
  // the ad, wired into the first beat (the edge is added after beat-1 exists, below). Same x-column as
  // F0 (fixed position, so it does NOT advance `order`), position preserved by id across edits.
  const hasBrand = Boolean(opts.brandLogoUrl || opts.brandDomain);
  if (hasBrand) {
    const brandId = 'brand';
    const oldBrand = prevById.get(brandId);
    nodes.push({
      id: brandId,
      type: 'brand',
      // Up-left of beat 0 (F0 now hangs directly above beat 0), feeding the first beat from its left handle.
      position: { x: BRAND_X, y: kvAboveY },
      selected: oldBrand?.selected,
      measured: oldBrand?.measured,
      data: { logoUrl: opts.brandLogoUrl, domain: opts.brandDomain, status: originStatus },
    });
  }

  let prevId = f0Id;
  for (let i = 0; i < n; i += 1) {
    const beat = draft.beats[i];
    const j = i + 1; // this beat/frame's 1-based index (matches the stage ids and generatedFrames slot)
    // Base run status from the live stage; then, if the stage hasn't already made it active/done, a
    // beat at or past a regenerate/resume's `fromBeat` reads as `stale` (queued to be re-run).
    const base = beatFrameStatus(j, running, run);
    const stale = opts.staleFromBeat != null && j >= opts.staleFromBeat;
    // Compose loader overrides the run status: while the storyboard is being written, a beat is a shimmer
    // skeleton until its turn (seeding = every beat; reveal = beats after the cursor), 'typing' (active)
    // while its motion prose animates in, or done once revealed. deriveCompose returns the mode + node
    // status + inbound-edge status together, keeping the seeding "whole chain pulses" case in one place.
    const composeBeat = compose ? deriveComposeBeat(i, compose) : undefined;
    const status: NodeStatus = composeBeat
      ? composeBeat.status
      : base === 'active' || base === 'done'
        ? base
        : stale
          ? 'stale'
          : base;
    // This beat's clip (segments[j]); '' (not yet rendered) is treated as absent for the view affordance.
    const clipUrl = opts.segments?.[j] || undefined;
    // The beat's end frame (generatedFrames[j]) doubles as its video-preview poster. Frames are no
    // longer their own nodes — the beat card carries the preview.
    const poster = generatedFrames?.[j] || undefined;
    // The promotion this beat advertises (clamped defensively), and whether it OPENS that promotion's run.
    const pIdx = clampPromo(beat.promotion_index ?? 0);
    if (!promoFirstBeat.has(pIdx)) promoFirstBeat.set(pIdx, i);

    const beatId = `beat-${j}`;
    nodes.push({
      id: beatId,
      type: 'beat',
      ...carryOver(beatId, BEAT_Y),
      data: {
        index: i,
        beat,
        canRemove: n > STORYBOARD_MIN_BEATS,
        canMoveLeft: i > 0,
        canMoveRight: i < n - 1,
        status,
        clipUrl,
        poster,
        running,
        thumbRatio,
        // Pin a failed run's reason to the beat it stopped on, so the owner sees WHERE it failed.
        error: opts.failedBeat === j ? opts.beatError : undefined,
        composing: composeBeat?.mode,
        typedMotion:
          composeBeat?.mode === 'typing' ? beat.motion_intent.slice(0, Math.max(0, compose?.typed ?? 0)) : undefined,
        promotionIndex: multiPromo ? pIdx : undefined,
        promotionName: promoName(pIdx),
      },
    });
    // Chain beat -> beat directly: beat i animates from where beat i-1's clip actually ended (the
    // worker's frame-accurate seam), so no frame node sits between them. The inbound edge uses the compose
    // edge status (the whole chain pulses while seeding; only the typing beat's edge animates on reveal).
    // First link comes DOWN from F0 (above beat 0) into the beat's top handle; the rest chain left-to-right.
    edges.push(edge(prevId, beatId, composeBeat ? composeBeat.edge : status, 'out', i === 0 ? 'kv' : 'in'));
    order += 1;
    prevId = beatId;
  }

  // Wire the brand card into the first beat (both the key visual and the brand feed beat 1).
  if (hasBrand && n >= 1) {
    edges.push(edge('brand', 'beat-1', running ? 'done' : 'idle', undefined, 'in'));
  }

  // Multi-promo: every promotion after the first (F0) is its own read-only key-visual card that drops in
  // from ABOVE the beat where its run begins, connected to that beat — so the owner sees each promotion's
  // artwork entering the timeline at its transition point. A promotion with no beats assigned (fewer beats
  // than promotions) gets no card. Positions are pinned (non-draggable), aspect-aware to clear the beat row.
  if (multiPromo) {
    for (let p = 1; p < promotions.length; p += 1) {
      const b = promoFirstBeat.get(p);
      if (b == null) continue;
      const kvId = `kv-${p}`;
      const oldKv = prevById.get(kvId);
      nodes.push({
        id: kvId,
        type: 'frame',
        // Directly above its beat (beat b is 0-based at order b = x NODE_GAP_X*b), feeding it top-down.
        position: { x: NODE_GAP_X * b, y: kvAboveY },
        selected: oldKv?.selected,
        measured: oldKv?.measured,
        draggable: false,
        data: {
          index: -1,
          imageUrl: promotions[p]?.image_url,
          source: 'keyArt',
          locked: true,
          status: originStatus,
          running,
          thumbRatio,
          promotionIndex: p,
          promotionName: promoName(p),
        },
      });
      edges.push(edge(kvId, `beat-${b + 1}`, originStatus, 'out', 'kv'));
    }
  }

  // Total length is the sum of the beats — there is no separate end-card (the last beat is the outro).
  const seconds = draft.beats.reduce((sum, b) => sum + b.duration_seconds, 0);
  const outputId = 'output';
  const outStatus = outputStatus(running, run);
  nodes.push({
    id: outputId,
    type: 'output',
    ...carryOver(outputId, OUTPUT_Y),
    data: {
      aspect: draft.aspect,
      aspects: opts.videoAspects,
      cta: draft.cta,
      beats: n,
      seconds,
      status: outStatus,
      versions: opts.finishedAd?.versions,
      selectedVersionKey: opts.finishedAd?.selectedVersionKey,
      poster: opts.finishedAd?.poster,
      downloadName: opts.finishedAd?.downloadName,
    },
  });
  edges.push(edge(prevId, outputId, outStatus, 'out', undefined));

  return { nodes, edges };
};
